import React, { useState } from 'react';
import { X, Link as LinkIcon, Download, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';

// 간단 CSV 파서 (따옴표 포함 처리)
const parseCsv = (csvText) => {
    const rows = [];
    let i = 0, field = '', row = [], inQuotes = false;
    while (i < csvText.length) {
        const char = csvText[i];
        if (inQuotes) {
            if (char === '"') {
                if (csvText[i + 1] === '"') { // 이스케이프된 따옴표
                    field += '"';
                    i += 2;
                    continue;
                } else {
                    inQuotes = false;
                    i++;
                    continue;
                }
            } else {
                field += char;
                i++;
                continue;
            }
        } else {
            if (char === '"') {
                inQuotes = true;
                i++;
                continue;
            }
            if (char === ',') {
                row.push(field);
                field = '';
                i++;
                continue;
            }
            if (char === '\n') {
                row.push(field);
                rows.push(row);
                row = [];
                field = '';
                i++;
                continue;
            }
            if (char === '\r') { i++; continue; }
            field += char;
            i++;
        }
    }
    // 마지막 필드/행 처리
    if (field.length > 0 || row.length > 0) {
        row.push(field);
        rows.push(row);
    }
    return rows;
};

const toHeaderMap = (headerRow) => {
    const map = {};
    headerRow.forEach((h, idx) => {
        map[String(h || '').trim()] = idx;
    });
    return map;
};

const normalizePhone = (phone) => {
    if (!phone) return '';
    const digits = String(phone).replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('010')) {
        return `${digits.slice(0, 3)}-${digits.slice(3, 7)}-${digits.slice(7)}`;
    }
    return digits;
};

const toISODate = (v) => {
    if (!v) return '';
    const s = String(v).trim();
    // 지원: YYYY-MM-DD, YYYY.MM.DD, YYYY/MM/DD
    const norm = s.replace(/[./]/g, '-');
    const d = new Date(norm);
    return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
};

const toISODateTimeKST = (v) => {
    if (!v) return '';
    const s = String(v).trim();

    // 다양한 날짜 형식 처리
    let dateStr = s;

    // 실제 데이터 형식들 처리:
    // "2024-03-20 7:08:16" (한 자리 시간)
    // "2024-04-10 9:27:55" (한 자리 시간)
    // "2024-01-10 9:33:00" (한 자리 시간)
    // "2025-01-09 16:19:18" (두 자리 시간)

    // 날짜 구분자를 통일
    dateStr = dateStr.replace(/[./]/g, '-');

    // T 형식을 공백으로 변경
    dateStr = dateStr.replace('T', ' ');

    // 밀리초 제거
    dateStr = dateStr.replace(/\.\d{3}/, '');

    // 시간이 없는 경우 기본 시간 추가
    if (!dateStr.includes(' ')) {
        dateStr += ' 00:00:00';
    }

    // 한 자리 시간을 두 자리로 보정 (예: "7:08:16" → "07:08:16")
    dateStr = dateStr.replace(/(\d{4}-\d{2}-\d{2}) (\d{1}):(\d{2}):(\d{2})/, '$1 0$2:$3:$4');

    // 초가 없는 경우 추가
    if (dateStr.match(/\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/)) {
        dateStr += ':00';
    }

    // 한국 시간대로 파싱 시도
    let d = new Date(dateStr);

    // 첫 번째 시도가 실패하면 다른 형식으로 시도
    if (isNaN(d.getTime())) {
        // "YYYY-MM-DD HH:mm:ss" 형식으로 다시 시도
        const parts = dateStr.split(' ');
        if (parts.length === 2) {
            const [datePart, timePart] = parts;
            const [year, month, day] = datePart.split('-');
            const [hour, minute, second] = timePart.split(':');

            if (year && month && day && hour && minute) {
                d = new Date(parseInt(year), parseInt(month) - 1, parseInt(day),
                    parseInt(hour), parseInt(minute), parseInt(second || 0));
            }
        }
    }

    if (isNaN(d.getTime())) {
        console.warn('날짜 파싱 실패:', s, '→', dateStr);
        return '';
    }

    // 한국 시간대로 변환하여 ISO 문자열 반환
    return d.toISOString();
};

const extractSpreadsheetId = (url) => {
    const m = String(url).match(/spreadsheets\/(d|u\/\d\/d)\/([a-zA-Z0-9-_]+)/);
    return m ? m[2] : '';
};

const buildCsvUrlBySheetName = (spreadsheetId, sheetName) => {
    // gviz csv export by sheet name
    const encoded = encodeURIComponent(sheetName);
    return `https://docs.google.com/spreadsheets/d/${spreadsheetId}/gviz/tq?tqx=out:csv&sheet=${encoded}`;
};


const ImportLegacyModal = ({ show, onClose, onImported, defaultSheetUrl = '' }) => {
    const [sheetUrl, setSheetUrl] = useState(defaultSheetUrl);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);
    const [phase, setPhase] = useState('idle'); // 'parse' | 'customers' | 'history' | 'save'
    const [progress, setProgress] = useState({ totalCustomers: 0, processedCustomers: 0, totalHistory: 0, processedHistory: 0 });
    const [importResults, setImportResults] = useState(null);

    const fetchCsv = async (url) => {
        try {
            const res = await fetch(url);
            if (!res.ok) {
                throw new Error('access');
            }
            return await res.text();
        } catch (_) {
            // 네트워크 실패(Failed to fetch), 권한 문제(403/404) 등을 단일 메시지로 안내
            throw new Error('해당파일의 접근권한이 없습니다');
        }
    };

    const importData = async () => {
        if (loading) return; // 중복 실행 방지
        setError('');
        setDone(false);
        try {
            if (!sheetUrl) throw new Error('소스파일 링크를 입력해주세요.');
            const spreadsheetId = extractSpreadsheetId(sheetUrl);
            if (!spreadsheetId) throw new Error('유효한 소스파일 링크가 아닙니다.');

            setLoading(true);
            setPhase('parse');
            // 고정 시트명 사용 (사용자 입력 필드 제거)
            const customerCsvUrl = buildCsvUrlBySheetName(spreadsheetId, '고객목록');
            const historyCsvUrl = buildCsvUrlBySheetName(spreadsheetId, '히스토리');

            const [customerCsv, historyCsv] = await Promise.all([
                fetchCsv(customerCsvUrl),
                fetchCsv(historyCsvUrl)
            ]);

            // 파싱
            const custRows = parseCsv(customerCsv).filter(r => r.length && r.some(c => String(c).trim().length > 0));
            const histRows = parseCsv(historyCsv).filter(r => r.length && r.some(c => String(c).trim().length > 0));

            // 소스 파일 데이터 개수 로그


            if (custRows.length < 2) throw new Error('고객목록 CSV에 데이터가 없습니다.');
            if (histRows.length < 2) throw new Error('히스토리 CSV에 데이터가 없습니다.');

            const custHeader = toHeaderMap(custRows[0]);
            const histHeader = toHeaderMap(histRows[0]);
            // 고객 식별자 헤더는 유연하게 허용
            const candidateIdHeaders = ['ID', '고객ID', '고객 Id', '고객id', '고객 아이디'];
            const custIdHeader = candidateIdHeaders.find(h => h in custHeader);
            const requiredCust = ['고객명', '핸드폰', '생년월일', '메모'];
            const requiredHist = ['고객ID', '메모일시', '내용'];
            if (!custIdHeader) throw new Error('고객목록에 식별자 필드(ID/고객ID)가 없습니다.');
            for (const h of requiredCust) if (!(h in custHeader)) throw new Error(`고객목록에 필드가 없습니다: ${h}`);
            for (const h of requiredHist) if (!(h in histHeader)) throw new Error(`히스토리에 필드가 없습니다: ${h}`);

            // 기존 고객 데이터 초기화 (IndexedDB에서 로드할 예정)
            const customersData = { prospect: [], preparation: [], appointment: [], consultation: [], closed: [], rejected: [], bank: [] };

            // 레거시ID → 신규ID 매핑 (UUID 사용)
            const legacyToNewId = {};

            // 고객 생성: 단순 처리
            setPhase('customers');
            const totalCustomers = custRows.length - 1;
            setProgress(p => ({ ...p, totalCustomers, processedCustomers: 0 }));

            let customersProcessed = 0;

            for (let i = 1; i < custRows.length; i++) {
                const row = custRows[i];
                const legacyId = String(row[custHeader[custIdHeader]] || '').trim();
                if (!legacyId) {
                    continue;
                }

                const name = String(row[custHeader['고객명']] || '').trim();
                const phone = normalizePhone(row[custHeader['핸드폰']]);
                const birthdate = toISODate(row[custHeader['생년월일']]);
                const memo = String(row[custHeader['메모']] || '').trim();

                // 새로운 필드들 (선택적)
                const keyword = custHeader['키워드'] !== undefined ? String(row[custHeader['키워드']] || '').trim() : '';
                const address = custHeader['주소'] !== undefined ? String(row[custHeader['주소']] || '').trim() : '';
                const detailAddress = custHeader['상세주소'] !== undefined ? String(row[custHeader['상세주소']] || '').trim() : '';
                const relation = custHeader['관계'] !== undefined ? String(row[custHeader['관계']] || '').trim() : '';

                // 필수 데이터 검증
                if (!name || !phone) {

                    continue;
                }

                const newId = crypto.randomUUID();
                const newCustomer = {
                    id: newId,
                    name,
                    phone,
                    birthdate,
                    memo,
                    keyword,
                    address,
                    detailAddress,
                    relation,
                    isImported: true
                };

                customersData.bank.push(newCustomer);
                legacyToNewId[legacyId] = newId;
                customersProcessed++;

                setProgress(p => ({ ...p, processedCustomers: i }));
            }



            // 히스토리 저장소 초기화 (IndexedDB에서 로드할 예정)
            const historyStore = {};

            // 히스토리: 단순 처리
            setPhase('history');
            const totalHistory = histRows.length - 1;
            setProgress(p => ({ ...p, totalHistory, processedHistory: 0 }));
            let historyProcessed = 0;

            for (let i = 1; i < histRows.length; i++) {
                const row = histRows[i];

                // 빈 행 건너뛰기
                if (!row || row.length === 0 || !row.some(cell => String(cell || '').trim())) {
                    continue;
                }

                const legacyCustId = String(row[histHeader['고객ID']] || '').trim();
                const atRaw = row[histHeader['메모일시']];
                const noteRaw = row[histHeader['내용']];
                const fuDateRaw = histHeader['FU계획일자'] !== undefined ? row[histHeader['FU계획일자']] : null;
                const fuContentRaw = histHeader['FU내용'] !== undefined ? row[histHeader['FU내용']] : null;
                const newId = legacyToNewId[legacyCustId];



                if (!newId) {
                    continue;
                }

                const at = toISODateTimeKST(atRaw);
                const note = String(noteRaw || '').trim();
                const fuDate = fuDateRaw ? toISODate(fuDateRaw) : null;
                const fuContent = fuContentRaw ? String(fuContentRaw || '').trim() : null;

                // 날짜 파싱이 실패했거나 메모가 비어있으면 건너뛰기
                if (!at || !note) {
                    continue;
                }

                // 히스토리 엔트리 생성
                const historyEntry = {
                    id: crypto.randomUUID(),
                    type: 'note',
                    note,
                    at,
                    customerId: newId
                };

                // FU계획이 있으면 추가
                if (fuDate && fuContent) {
                    historyEntry.followUp = {
                        date: fuDate,
                        content: fuContent
                    };
                }

                const key = String(legacyCustId);
                const list = historyStore[key] || [];
                list.unshift(historyEntry);
                historyStore[key] = list;
                historyProcessed++;

                setProgress(p => ({ ...p, processedHistory: i }));
            }



            // IndexedDB 저장 (안정 모드: 기존 데이터 초기화 후 덮어쓰기)
            setPhase('save');
            try {
                // 세이프티 타임아웃: 20초 후 강제 해제
                const safety = setTimeout(() => {
                    try {
                        console.warn('⏱️ 임포트 저장이 지연되어 로딩을 해제합니다.');
                        setLoading(false);
                        setPhase('idle');
                    } catch (_) { }
                }, 20000);

                const {
                    openDb,
                    idbClearCustomers,
                    idbClearHistories,
                    idbPutCustomers,
                    idbPutHistories,
                    idbSetSetting
                } = await import('../utils/idb');

                await openDb();


                // 고객 데이터를 IndexedDB 형식으로 변환
                const importedCustomers = Object.entries(customersData).flatMap(([stage, list]) =>
                    (list || []).map(c => ({ ...c, stage }))
                );
                console.log('💾 [SAVE] 임포트 고객 변환 완료:', importedCustomers.length);

                // 히스토리 데이터를 IndexedDB 형식으로 변환 (레거시 ID → UUID 매핑)


                const importedHistories = Object.entries(historyStore).flatMap(([legacyCustomerId, list]) => {
                    const newCustomerId = legacyToNewId[legacyCustomerId];


                    if (!newCustomerId) {

                        return [];
                    }
                    return (list || []).map(h => ({
                        ...h,
                        customerId: newCustomerId
                    }));
                });




                // 기존 데이터 초기화 후 덮어쓰기 저장

                await idbClearCustomers();
                await idbClearHistories();



                await idbPutCustomers(importedCustomers);


                await idbPutHistories(importedHistories);


                // 마이그레이션 완료 플래그 설정
                await idbSetSetting('customers_migrated', true);
                await idbSetSetting('histories_migrated', true);

                const finalResults = {
                    소스파일_고객수: totalCustomers,
                    소스파일_히스토리수: totalHistory,
                    처리된_고객수: customersProcessed,
                    처리된_히스토리수: historyProcessed,
                    기존_고객수: 0,
                    추가된_고객수: importedCustomers.length,
                    건너뛴_고객수: 0,
                    최종_총고객수: importedCustomers.length,
                    기존_히스토리수: 0,
                    추가된_히스토리수: importedHistories.length,
                    최종_총히스토리수: importedHistories.length,
                    고객_처리율: `${((customersProcessed / totalCustomers) * 100).toFixed(1)}%`,
                    히스토리_처리율: `${((historyProcessed / totalHistory) * 100).toFixed(1)}%`
                };

                console.log('✅ 최종 임포트 결과 요약:', {
                    '=== 소스 파일 데이터 ===': '',
                    소스파일_고객수: totalCustomers,
                    소스파일_히스토리수: totalHistory,
                    '=== 처리 결과 ===': '',
                    처리된_고객수: customersProcessed,
                    처리된_히스토리수: historyProcessed,
                    '=== 최종 저장 결과 ===': '',
                    기존_고객수: 0,
                    추가된_고객수: importedCustomers.length,
                    건너뛴_고객수: 0,
                    최종_총고객수: importedCustomers.length,
                    기존_히스토리수: 0,
                    추가된_히스토리수: importedHistories.length,
                    최종_총히스토리수: importedHistories.length,
                    '=== 처리율 ===': '',
                    고객_처리율: `${((customersProcessed / totalCustomers) * 100).toFixed(1)}%`,
                    히스토리_처리율: `${((historyProcessed / totalHistory) * 100).toFixed(1)}%`
                });

                setImportResults(finalResults);


                // 저장 완료 후 즉시 콜백 호출
                clearTimeout(safety);
                setDone(true);
                if (typeof onImported === 'function') {
                    console.log('🔄 임포트 완료, 데이터 새로고침 중...');
                    onImported();
                }

            } catch (e) {
                console.error('IndexedDB 저장 실패:', e);
                throw new Error('데이터 저장에 실패했습니다.');
            }
        } catch (e) {
            setError(e.message || '임포트 중 오류가 발생했습니다.');
        } finally {
            setLoading(false);
            setPhase('idle');
        }
    };

    if (!show) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl max-w-md w-full max-h-[85vh] overflow-hidden shadow-2xl border border-white/20">
                <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                            <Download className="w-5 h-5 text-white" />
                        </div>
                        <h2 className="text-lg font-bold text-gray-800">영업의신세계 데이터 임포트</h2>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-white/70 rounded-xl transition-colors">
                        <X className="w-5 h-5 text-gray-600" />
                    </button>
                </div>

                <div className="p-6 space-y-4 overflow-y-auto max-h-[58vh]">
                    {loading && (
                        <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-700">
                            <div className="font-semibold mb-3 flex items-center gap-2">
                                <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                                데이터 가져오는 중…
                            </div>
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="font-medium">
                                        {phase === 'parse' ? '📄 데이터 파싱' :
                                            phase === 'customers' ? '👥 고객 데이터 처리' :
                                                phase === 'history' ? '📝 히스토리 데이터 처리' :
                                                    phase === 'save' ? '💾 IndexedDB 저장' : '⏳ 대기 중'}
                                    </span>
                                    <span className="text-xs text-blue-600">
                                        {phase === 'customers' ? `${Math.round((progress.processedCustomers / progress.totalCustomers) * 100)}%` :
                                            phase === 'history' ? `${Math.round((progress.processedHistory / progress.totalHistory) * 100)}%` :
                                                phase === 'save' ? '100%' : '0%'}
                                    </span>
                                </div>

                                {phase === 'customers' && (
                                    <div>
                                        <div className="flex justify-between text-xs mb-1">
                                            <span>고객 데이터</span>
                                            <span>{progress.processedCustomers}/{progress.totalCustomers}</span>
                                        </div>
                                        <div className="w-full h-2 bg-blue-100 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-300"
                                                style={{ width: `${progress.totalCustomers ? Math.round((progress.processedCustomers / progress.totalCustomers) * 100) : 0}%` }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {phase === 'history' && (
                                    <div>
                                        <div className="flex justify-between text-xs mb-1">
                                            <span>히스토리 데이터</span>
                                            <span>{progress.processedHistory}/{progress.totalHistory}</span>
                                        </div>
                                        <div className="w-full h-2 bg-blue-100 rounded-full overflow-hidden">
                                            <div
                                                className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-300"
                                                style={{ width: `${progress.totalHistory ? Math.round((progress.processedHistory / progress.totalHistory) * 100) : 0}%` }}
                                            />
                                        </div>
                                    </div>
                                )}

                                {phase === 'save' && (
                                    <div>
                                        <div className="flex justify-between text-xs mb-1">
                                            <span>데이터 저장</span>
                                            <span>완료</span>
                                        </div>
                                        <div className="w-full h-2 bg-blue-100 rounded-full overflow-hidden">
                                            <div className="h-full bg-gradient-to-r from-green-500 to-green-600 rounded-full" />
                                        </div>
                                    </div>
                                )}

                                <div className="text-xs text-blue-600 mt-2">
                                    💡 대용량 데이터 처리 중입니다. 잠시만 기다려주세요...
                                </div>
                            </div>
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-bold text-gray-700 mb-2">소스파일 링크</label>
                        <div className="relative">
                            <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="url"
                                value={sheetUrl}
                                onChange={(e) => setSheetUrl(e.target.value)}
                                disabled={loading}
                                className={`w-full pl-9 pr-3 py-3 border-2 rounded-xl focus:outline-none transition-all ${loading ? 'bg-gray-100 cursor-not-allowed border-gray-200' : 'border-gray-200 focus:border-blue-500'}`}
                            />
                        </div>
                        <p className="text-xs text-gray-500 mt-1">공개 또는 링크소지자 열람 권한 필요</p>
                    </div>



                    {error && (
                        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl">
                            <AlertTriangle className="w-4 h-4 mt-0.5" />
                            <div className="text-sm">{error}</div>
                        </div>
                    )}
                    {done && importResults && (
                        <div className="space-y-3">
                            <div className="flex items-start gap-2 p-3 bg-green-50 border border-green-200 text-green-700 rounded-xl">
                                <CheckCircle2 className="w-4 h-4 mt-0.5" />
                                <div>
                                    <p className="font-semibold">임포트 완료!</p>
                                    <p className="text-xs mt-1">데이터가 성공적으로 가져와졌습니다.</p>
                                </div>
                            </div>

                            {/* 임포트 결과 상세 정보 */}
                            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
                                <h3 className="font-semibold text-gray-800 text-sm">📊 임포트 결과 상세</h3>

                                <div className="grid grid-cols-2 gap-3 text-xs">
                                    <div className="space-y-2">
                                        <div className="font-medium text-gray-700">소스 파일 데이터</div>
                                        <div className="text-gray-600">
                                            <div>고객: {importResults.소스파일_고객수}명</div>
                                            <div>히스토리: {importResults.소스파일_히스토리수}개</div>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <div className="font-medium text-gray-700">처리 결과</div>
                                        <div className="text-gray-600">
                                            <div>고객: {importResults.처리된_고객수}명 ({importResults.고객_처리율})</div>
                                            <div>히스토리: {importResults.처리된_히스토리수}개 ({importResults.히스토리_처리율})</div>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <div className="font-medium text-gray-700">최종 저장</div>
                                        <div className="text-gray-600">
                                            <div>총 고객: {importResults.최종_총고객수}명</div>
                                            <div>총 히스토리: {importResults.최종_총히스토리수}개</div>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <div className="font-medium text-gray-700">추가된 데이터</div>
                                        <div className="text-gray-600">
                                            <div>고객: +{importResults.추가된_고객수}명</div>
                                            <div>히스토리: +{importResults.추가된_히스토리수}개</div>
                                        </div>
                                    </div>
                                </div>

                                {(importResults.소스파일_고객수 - importResults.처리된_고객수 > 0 ||
                                    importResults.소스파일_히스토리수 - importResults.처리된_히스토리수 > 0) && (
                                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-2">
                                            <div className="text-xs text-yellow-700">
                                                ⚠️ 일부 데이터가 처리되지 않았습니다. 콘솔 로그를 확인해주세요.
                                            </div>
                                        </div>
                                    )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="p-6 bg-gray-50 border-t border-gray-200 flex gap-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={loading}
                        className={`flex-1 py-3 px-4 rounded-xl font-bold transition-all ${loading ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                    >
                        취소
                    </button>
                    <button
                        type="button"
                        onClick={importData}
                        disabled={loading}
                        aria-busy={loading}
                        className={`flex-1 py-3 px-4 rounded-xl font-bold shadow-lg disabled:opacity-50 ${loading ? 'bg-blue-400 text-white cursor-progress' : 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700'}`}
                    >
                        {loading ? (
                            <span className="inline-flex items-center gap-2">
                                <Loader2 className="w-4 h-4 animate-spin" />
                                가져오는 중…
                            </span>
                        ) : '가져오기'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ImportLegacyModal;


